/**
 * Сообщения W3C-валидатора (vnu) по-русски и группировка повторов.
 *
 * Валидатор отвечает по-английски, а сообщение одного вида приходит
 * столько раз, сколько раз встретилось на странице: у сайтов на Next.js
 * это «Trailing slash on void elements» по разу на каждый <meta/> и
 * <img/>, 35 одинаковых строк подряд. Человеку нужно одно сообщение с
 * числом повторов и строками, где оно встретилось.
 *
 * Словарь покрывает частые сообщения. Незнакомое остаётся английским:
 * лучше непереведённая строка, чем потерянная ошибка разметки.
 */

const q = '[“"«]([^”"»]*)[”"»]';

const SHABLONY: [RegExp, (m: RegExpExecArray) => string][] = [
  [
    /^Trailing slash on void elements has no effect/,
    () =>
      'Косая черта в конце пустого тега (<meta />, <img />) ни на что не влияет. Безвредно, так выводят многие фреймворки.',
  ],
  [new RegExp(`^Duplicate ID ${q}\\.?$`), (m) => `Повторяется id «${g(m, 1)}».`],
  [
    new RegExp(`^The first occurrence of ID ${q} was here\\.?$`),
    (m) => `Здесь id «${g(m, 1)}» встретился впервые.`,
  ],
  [
    new RegExp(`^Element ${q} not allowed as child of element ${q} in this context`),
    (m) => `Тег <${g(m, 1)}> нельзя вкладывать в <${g(m, 2)}> в этом месте.`,
  ],
  [
    new RegExp(`^Bad value ${q} for (?:the )?attribute ${q} on element ${q}`),
    (m) => `Недопустимое значение «${g(m, 1)}» атрибута ${g(m, 2)} у тега <${g(m, 3)}>.`,
  ],
  [
    new RegExp(`^Bad value ${q} for (?:the )?attribute ${q}`),
    (m) => `Недопустимое значение «${g(m, 1)}» атрибута ${g(m, 2)}.`,
  ],
  [
    new RegExp(`^Attribute ${q} not allowed on element ${q} at this point`),
    (m) => `Атрибут ${g(m, 1)} не допускается у тега <${g(m, 2)}>.`,
  ],
  [
    new RegExp(`^Element ${q} is missing required attribute ${q}`),
    (m) => `У тега <${g(m, 1)}> нет обязательного атрибута ${g(m, 2)}.`,
  ],
  [
    new RegExp(`^Element ${q} is missing one or more of the following attributes: (.*)\\.?$`),
    (m) => `У тега <${g(m, 1)}> не хватает одного из атрибутов: ${g(m, 2)}.`,
  ],
  [
    /^An “img” element must have an “alt” attribute/,
    () => 'У картинки <img> должен быть атрибут alt.',
  ],
  [
    new RegExp(`^The ${q} attribute must refer to an element in the same document`),
    (m) => `Атрибут ${g(m, 1)} ссылается на элемент, которого нет на странице.`,
  ],
  [
    new RegExp(`^The ${q} attribute references ${q}, which is not the ID of any element`),
    (m) => `Атрибут ${g(m, 1)} ссылается на id «${g(m, 2)}», которого нет на странице.`,
  ],
  [
    new RegExp(`^The ${q} attribute must point to an element in the same document`),
    (m) => `Атрибут ${g(m, 1)} ссылается на элемент, которого нет на странице.`,
  ],
  [new RegExp(`^Stray end tag ${q}`), (m) => `Лишний закрывающий тег </${g(m, 1)}>.`],
  [new RegExp(`^Stray start tag ${q}`), (m) => `Тег <${g(m, 1)}> стоит вне допустимого места.`],
  [
    new RegExp(`^End tag ${q} seen, but there were open elements`),
    (m) => `Закрыт </${g(m, 1)}>, а вложенные теги остались открытыми.`,
  ],
  [new RegExp(`^Unclosed element ${q}`), (m) => `Тег <${g(m, 1)}> не закрыт.`],
  [
    /^Start tag seen without seeing a doctype first/,
    () => 'Нет <!DOCTYPE html> в начале страницы.',
  ],
  [
    new RegExp(`^The ${q} attribute on the ${q} element is obsolete`),
    (m) => `Атрибут ${g(m, 1)} у тега <${g(m, 2)}> устарел.`,
  ],
  [new RegExp(`^The ${q} element is obsolete`), (m) => `Тег <${g(m, 1)}> устарел.`],
  [
    /^The “type” attribute is unnecessary for JavaScript resources/,
    () => 'Атрибут type у скрипта не нужен.',
  ],
  [
    /^The “type” attribute for the “style” element is not needed/,
    () => 'Атрибут type у <style> не нужен.',
  ],
  [
    /^Consider adding a “lang” attribute to the “html” start tag/,
    () => 'Укажите язык страницы атрибутом lang у <html>.',
  ],
  [/^Section lacks heading/, () => 'У раздела <section> нет заголовка.'],
  [/^Article lacks heading/, () => 'У <article> нет заголовка.'],
  [/^Empty heading/, () => 'Пустой заголовок.'],
  [/^Duplicate attribute/, () => 'Атрибут повторяется в одном теге.'],
  [
    /^A document must not include more than one visible “main” element/,
    () => 'На странице больше одного видимого <main>.',
  ],
  [
    /^Possible misuse of “aria-label”/,
    () => 'aria-label, возможно, стоит на теге, которому он не нужен.',
  ],
  [
    /^The “banner” role is unnecessary for element “header”/,
    () => 'Роль banner у <header> лишняя.',
  ],
  [
    /^The “navigation” role is unnecessary for element “nav”/,
    () => 'Роль navigation у <nav> лишняя.',
  ],
  [
    /^The “contentinfo” role is unnecessary for element “footer”/,
    () => 'Роль contentinfo у <footer> лишняя.',
  ],
  [/^The “main” role is unnecessary for element “main”/, () => 'Роль main у <main> лишняя.'],
  [
    /^Illegal character in (?:path segment|query|fragment)/,
    () => 'Недопустимый символ в адресе (пробел или кириллица без кодирования).',
  ],
];

function g(m: RegExpExecArray, i: number): string {
  return m[i] ?? '';
}

/**
 * Хвост, который валидатор дописывает к сообщению и который человеку
 * ничего не даёт: «(Suppressing further errors from this subtree.)».
 */
const HVOST = /\s*\(Suppressing further errors from this subtree\.\)\s*$/;

/** Сообщение валидатора по-русски или как есть, если перевода нет. */
export function perevodW3c(message: string): string {
  const m0 = message.replace(HVOST, '');
  for (const [re, f] of SHABLONY) {
    const m = re.exec(m0);
    if (m) return f(m);
  }
  return m0;
}

export interface GruppaW3c {
  tekst: string;
  raz: number;
  stroki: number[];
}

/**
 * Одинаковые сообщения одной группой: перевод, число повторов и
 * строки, где они встретились (без повторов, по возрастанию).
 */
export function gruppirovatW3c(
  soobshcheniya: { message: string; line?: number }[] | null | undefined,
): GruppaW3c[] {
  const po = new Map<string, GruppaW3c>();
  for (const s of soobshcheniya ?? []) {
    const tekst = perevodW3c(s.message);
    let gr = po.get(tekst);
    if (!gr) {
      gr = { tekst, raz: 0, stroki: [] };
      po.set(tekst, gr);
    }
    gr.raz++;
    if (s.line && !gr.stroki.includes(s.line)) gr.stroki.push(s.line);
  }
  for (const gr of po.values()) gr.stroki.sort((a, b) => a - b);
  return [...po.values()];
}

/** Подпись строк: «строка 5», «строки 1, 5, 9» или «строки 1, 5, 9 и ещё 12». */
export function podpisStrok(stroki: number[], pokazat = 5): string {
  if (stroki.length === 0) return '';
  const vidno = stroki.slice(0, pokazat).join(', ');
  const eshche = stroki.length > pokazat ? ` и ещё ${String(stroki.length - pokazat)}` : '';
  return `${stroki.length === 1 ? 'строка' : 'строки'} ${vidno}${eshche}`;
}
